import { ofType, combineEpics } from 'redux-observable';
import { from } from 'rxjs';
import { mergeMap, filter, map } from 'rxjs/operators';
import { createEffect } from 'utils/epics';

import { getSocialRsvpFieldPrepopulatedId, getFormNodeByName } from 'common/entities/formNode/selectors';
import { NODE_NAME_FIRST_NAME, NODE_NAME_LAST_NAME, NODE_NAME_EMAIL } from 'common/entities/formNode/nodeNames';
import { LINKEDIN_RECEIVE_MESSAGE } from '../constants/epicTypes';

import { updateFormValue, setFormMeta } from '../actions/form';

const FACEBOOK_RECEIVE_MESSAGE = 'FACEBOOK_RECEIVE_MESSAGE';

export const linkedInReceiveMessage = createEffect(LINKEDIN_RECEIVE_MESSAGE);
export const facebookReceiveMessageEffect = createEffect(FACEBOOK_RECEIVE_MESSAGE);

const linkedInMessageEpic = (action$, state$) =>
    action$.pipe(
        ofType(LINKEDIN_RECEIVE_MESSAGE),
        filter(({ meta: { errorMessage } }) => errorMessage),
        map(({ meta: { errorMessage, nodeId } }) => setFormMeta(nodeId, { linkedIn: errorMessage })),
    );

const linkedInSetUserDataEpic = (action$, state$) =>
    action$.pipe(
        ofType(LINKEDIN_RECEIVE_MESSAGE),
        filter(({ meta: { canSetUserData } }) => canSetUserData),
        mergeMap(({ meta: { userData = {}, nodeId } }) => {
            const {
                firstName,
                lastName,
                email,
                linkedInCompany,
                linkedInJobTitle,
                linkedInProfileUrl,
                linkedInProfilePicture,
                linkedInLocation,
            } = userData;
            return from([
                updateFormValue(nodeId, {
                    value: ['linkedin'],
                    linked_in_first_name: firstName,
                    linked_in_last_name: lastName,
                    linked_in_email: email,
                    linked_in_company: linkedInCompany,
                    linked_in_job_title: linkedInJobTitle,
                    linked_in_profile_url: linkedInProfileUrl,
                    linked_in_profile_picture: linkedInProfilePicture,
                    linked_in_location: linkedInLocation,
                }),
                setFormMeta(nodeId, { requiredError: false }),
            ]);
        }),
    );

const socialRsvpFields = [
    'linkedInCompany',
    'linkedInJobTitle',
    'linkedInProfileUrl',
    'linkedInProfilePicture',
    'linkedInLocation',
    'facebookFacebookId',
    'facebookMiddleName',
    'facebookShortName',
];

const guestInfoSocialRsvpFields = [
    {
        nodeName: NODE_NAME_FIRST_NAME,
        field: 'firstName',
    },
    {
        nodeName: NODE_NAME_LAST_NAME,
        field: 'lastName',
    },
    {
        nodeName: NODE_NAME_EMAIL,
        field: 'email',
    },
];

const prepopulateSocialRsvpDataEpic = (action$, state$) =>
    action$.pipe(
        ofType(LINKEDIN_RECEIVE_MESSAGE, FACEBOOK_RECEIVE_MESSAGE),
        filter(({ meta: { canSetUserData } }) => canSetUserData),
        mergeMap(({ meta: { userData = {}, formId } }) => {
            const prepopulatedGuestInfoNodesActions = guestInfoSocialRsvpFields
                .filter(({ nodeName, field }) => Boolean(userData[field])) // exclude empty
                .map(({ nodeName, field }) => {
                    const node = getFormNodeByName(state$.value, formId, nodeName);
                    return updateFormValue(node.id, userData[field]);
                });

            const prepopulatedCustomNodesActions = socialRsvpFields
                .filter((fieldId) => Boolean(userData[fieldId])) // exclude empty
                .map((fieldId) => {
                    const prepopulatedId = getSocialRsvpFieldPrepopulatedId(state$.value, formId, fieldId);
                    return prepopulatedId ? updateFormValue(prepopulatedId, userData[fieldId]) : false;
                })
                .filter(Boolean);

            return from([...prepopulatedGuestInfoNodesActions, ...prepopulatedCustomNodesActions]);
        }),
    );

const facebookSetUserDataEpic = (action$, state$) =>
    action$.pipe(
        ofType(FACEBOOK_RECEIVE_MESSAGE),
        mergeMap(({ meta: { userData = {}, nodeId } }) => {
            const { firstName, lastName, email, facebookFacebookId, facebookMiddleName, facebookShortName } = userData;
            return from([
                updateFormValue(nodeId, {
                    value: ['facebook'],
                    facebook_first_name: firstName,
                    facebook_last_name: lastName,
                    facebook_email: email,
                    facebook_facebook_id: facebookFacebookId,
                    facebook_middle_name: facebookMiddleName,
                    facebook_short_name: facebookShortName,
                }),
                setFormMeta(nodeId, { requiredError: false }),
            ]);
        }),
    );

export default combineEpics(
    linkedInMessageEpic,
    linkedInSetUserDataEpic,
    prepopulateSocialRsvpDataEpic,
    facebookSetUserDataEpic,
);
